
import * as React from "react";
import { format, parse } from "date-fns";
import { fr } from "date-fns/locale";
import { Calendar as CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface DatePickerProps {
  selected?: Date;
  onSelect: (date: Date | undefined) => void;
  placeholder?: string;
}

export function DatePicker({ selected, onSelect, placeholder }: DatePickerProps) {
  const [inputValue, setInputValue] = React.useState("");

  // Mettre à jour l'input quand la date sélectionnée change
  React.useEffect(() => {
    if (selected) {
      setInputValue(format(selected, "dd/MM/yyyy"));
    }
  }, [selected]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);

    // Essayer de parser la date
    try {
      const parsedDate = parse(value, "dd/MM/yyyy", new Date());
      if (parsedDate.toString() !== "Invalid Date") {
        onSelect(parsedDate);
      }
    } catch (error) {
      // Si le format n'est pas valide, ne rien faire
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <div className="relative w-full">
          <Input
            value={inputValue}
            onChange={handleInputChange}
            placeholder={placeholder || "JJ/MM/AAAA"}
            className="w-full pl-10"
          />
          <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={selected}
          onSelect={(date) => {
            onSelect(date);
            if (date) {
              setInputValue(format(date, "dd/MM/yyyy"));
            }
          }}
          initialFocus
          locale={fr}
        />
      </PopoverContent>
    </Popover>
  );
}
